import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchMe,
  login as loginApi,
  logout as logoutApi,
  register as registerApi,
  type AuthPayload,
  type RegisterPayload,
} from "../api/auth";
import { syncAppBadge } from "../lib/appBadge";
import type { MeResponse, MeUser, UserRole } from "../api/types";

export const HOME_BY_ROLE: Record<UserRole, string> = {
  PATIENT: "/pasien",
  THERAPIST: "/therapist",
  TENANT_ADMIN: "/admin",
  SUPER_ADMIN: "/platform",
};

interface AuthContextValue {
  me?: MeResponse;
  user?: MeUser;
  isLoading: boolean;
  isAuthed: boolean;
  homePath: string;
  login: (payload: AuthPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<MeUser>;
  logoutNow: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  });

  const me = meQuery.data;

  const loginMutation = useMutation({
    mutationFn: loginApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  const registerMutation = useMutation({
    mutationFn: registerApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      queryClient.clear();
      // Badge ikon aplikasi ikut dibersihkan setelah keluar.
      syncAppBadge(0);
    },
  });

  const value = useMemo<AuthContextValue>(() => {
    const user = me?.user;
    return {
      me,
      user,
      isLoading: meQuery.isLoading,
      isAuthed: !!user,
      homePath: user ? HOME_BY_ROLE[user.role] : "/login",
      login: async (payload) => {
        await loginMutation.mutateAsync(payload);
      },
      register: async (payload) => (await registerMutation.mutateAsync(payload)).user,
      logoutNow: async () => {
        await logoutMutation.mutateAsync();
      },
    };
  }, [me, meQuery.isLoading, loginMutation, registerMutation, logoutMutation]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth wajib dipakai di dalam AuthProvider");
  return ctx;
}
